import React, { useState } from "react";
import { Navigate } from "react-router-dom";
import { Button } from "primereact/button";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { ProgressSpinner } from "primereact/progressspinner";
import toast from "react-hot-toast";
import { ReportPeriodFilter } from "../../components/Filters/ReportPeriodFilter";
import { useExportEventsReportMutation } from "../../store/services/reports";
import { useTenantScope } from "../../hooks/useTenantScope";
import { useReportPeriod } from "../../hooks/useReportPeriod";
import {
  DEFAULT_EVENT_REPORT_SORT_FIELD,
  EVENT_REPORT_SORT_FIELD_OPTIONS,
  EventReportSortField,
  eventReportDateRangeExceededMessage,
  isEventReportDateRangeWithinLimit,
} from "../../utils/constants/event-report-constants";
import { toReportDateParam } from "../../utils/constants/report-date-range";
import { getApiErrorMessage } from "../../utils/functions/api-errors";
import { downloadBlob, buildAnalyticsExportFilename } from "../../utils/functions/downloadBlob";
import { useTenantOptions } from "../../hooks/useTenantOptions";

export const Dashboard: React.FC = () => {
  const scope = useTenantScope();
  const { options: tenantOptions } = useTenantOptions();
  const tenantId = scope.tenantId;

  // Misma fila de períodos que el resto de los reportes. El export no agrupa
  // por bucket (es una fila por evento), así que ignora `grain`: los presets le
  // sirven solo como rango.
  const period = useReportPeriod();
  const { dateFrom, dateTo, rangeIsValid } = period;

  const [sortBy, setSortBy] = useState<EventReportSortField>(
    DEFAULT_EVENT_REPORT_SORT_FIELD
  );

  const [exportReport, { isLoading: isExporting }] = useExportEventsReportMutation();

  const canExport = rangeIsValid && !isExporting && !scope.loading;

  const handleSortByChange = (event: DropdownChangeEvent) => {
    setSortBy(event.value ?? DEFAULT_EVENT_REPORT_SORT_FIELD);
  };

  const handleExport = async () => {
    const fromStr = toReportDateParam(dateFrom);
    const toStr = toReportDateParam(dateTo);

    // El rango sale de un preset o de los selectores, que ya se acotan entre
    // sí; esto queda como red de seguridad antes de pegarle a la API.
    if (fromStr > toStr) {
      toast.error("La fecha desde no puede ser mayor que la fecha hasta");
      return;
    }

    if (!isEventReportDateRangeWithinLimit(dateFrom, dateTo)) {
      toast.error(eventReportDateRangeExceededMessage());
      return;
    }

    try {
      const result = await exportReport({
        tenantId,
        dateFrom: fromStr,
        dateTo: toStr,
        sortBy,
      }).unwrap();

      if (result.status === 204 || !result.blob || !result.filename) {
        toast("No hay datos para los filtros seleccionados", { icon: "ℹ️" });
        return;
      }

      downloadBlob(result.blob, buildAnalyticsExportFilename(result.filename, tenantOptions));
      toast.success("Reporte exportado");
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error) ?? "Error al exportar el reporte");
    }
  };

  return (
    /* Sin card propia: la pantalla es una sola card grande (ver ReportsConfig).
       `relative overflow-hidden` se queda porque ancla el overlay de exportación. */
    <div className="relative overflow-hidden">
        {isExporting && (
          <div
            className="absolute top-0 left-0 w-full h-full flex flex-column align-items-center justify-content-center border-round"
            style={{ backgroundColor: "rgba(255, 255, 255, 0.92)", zIndex: 10 }}
            role="status"
            aria-live="polite"
            aria-busy="true"
          >
            <ProgressSpinner style={{ width: "48px", height: "48px" }} strokeWidth="4" />
            <span className="mt-3 text-lg font-medium text-900">Generando reporte...</span>
          </div>
        )}

        {/* Qué hace el reporte va acá, debajo del título, como en el resto:
            antes estaba partido entre esta bajada y una leyenda pegada al botón
            de exportar, así que el mismo tipo de texto aparecía en dos lugares
            distintos según el reporte. */}
        <div className="mb-4">
          <h2 className="text-2xl font-semibold text-900 m-0">Reporte de eventos</h2>
          <p className="text-color-secondary mt-2 mb-0 line-height-3">
            Exportá un reporte consolidado de eventos por tenant y rango de fechas, en
            formato CSV. Incluye métricas de convocatoria, pagos, cancelaciones y
            valoraciones por evento.
          </p>
        </div>

        <ReportPeriodFilter period={period} disabled={isExporting} idPrefix="export" />

        <div className="grid">
          <div className="col-12 md:col-6 lg:col-4">
            <label htmlFor="export-sort-by" className="block font-medium mb-2">
              Ordenar por
            </label>
            <Dropdown
              id="export-sort-by"
              value={sortBy}
              onChange={handleSortByChange}
              options={EVENT_REPORT_SORT_FIELD_OPTIONS}
              optionLabel="label"
              optionValue="value"
              disabled={isExporting}
              className="w-full"
              aria-label="Ordenar por"
              data-testid="export-sort-by"
            />
          </div>
        </div>

        {/* Solo el botón, a la derecha: misma fila que los reportes de
            auditoría. */}
        <div className="flex flex-wrap align-items-center justify-content-end gap-3 mt-4 pt-3 border-top-1 surface-border">
          <Button
            type="button"
            label="Exportar CSV"
            icon="pi pi-file-excel"
            onClick={handleExport}
            disabled={!canExport}
            loading={isExporting}
            data-testid="export-events-report-button"
          />
        </div>
    </div>
  );
};

export const DashboardRedirect = () => {
  return <Navigate to="/reportes" replace />;
};
